import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';

import Layout, { ETAPES } from './Layout';
import { annulerDossier, creerDossier, lirePortefeuille } from './api';

const euros = (v) =>
  v === null || v === undefined
    ? '—'
    : `${Number(v).toLocaleString('fr-BE', { minimumFractionDigits: 2,
        maximumFractionDigits: 2 })} €`;

const NIVEAUX = {
  PROPRIETAIRE: 'Votre dossier',
  GESTION: 'Partagé — vous pouvez modifier',
  LECTURE: 'Partagé — lecture seule',
};

const titreEtape = (cle) =>
  ETAPES.find((e) => e.cle === cle)?.titre || cle;

/** Une ligne du portefeuille : l'état du dossier, et où le reprendre. */
function Dossier({ dossier, onOuvrir, onInviter, onAnnuler }) {
  const bloque = dossier.etape_manquante;
  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}
             alignItems={{ md: 'center' }} justifyContent="space-between">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="baseline"
                 flexWrap="wrap">
            <Typography variant="h3">
              {dossier.client || dossier.name}
            </Typography>
            {dossier.reference && (
              <Typography variant="body2" color="text.secondary">
                {dossier.reference}
              </Typography>
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" noWrap>
            {dossier.sinistre || dossier.name}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap"
                 useFlexGap>
            <Chip size="small" variant="outlined" label={dossier.statut} />
            <Chip size="small" variant="outlined"
                  label={NIVEAUX[dossier.niveau] || dossier.niveau} />
            <Chip size="small" variant="outlined"
                  label={`${dossier.dommages} dommage${dossier.dommages > 1 ? 's' : ''}`} />
            {bloque ? (
              <Chip size="small" color="warning" variant="outlined"
                    label={`À reprendre : ${titreEtape(bloque)}`} />
            ) : (
              <Chip size="small" color="success" variant="outlined"
                    label="Dossier complet" />
            )}
          </Stack>
        </Box>

        <Stack alignItems={{ md: 'flex-end' }} spacing={1}>
          <Typography variant="h3">{euros(dossier.montant)}</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant={bloque ? 'contained' : 'outlined'}
                    onClick={() => onOuvrir(dossier, bloque || 'dossier')}>
              {bloque ? 'Reprendre' : 'Ouvrir'}
            </Button>
            <Button variant="outlined" onClick={() => onOuvrir(dossier, 'suivi')}>
              Suivi
            </Button>
            <Button variant="outlined" onClick={() => onInviter(dossier)}>
              Inviter
            </Button>
            {/* Annuler est une décision du propriétaire : un dossier qu'on
                nous partage ne nous appartient pas. */}
            {dossier.niveau === 'PROPRIETAIRE' && (
              <Button color="inherit" onClick={() => onAnnuler(dossier)}>
                Annuler
              </Button>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  );
}

/**
 * Le portefeuille d’un intermédiaire.
 *
 * Un intermédiaire qui suit trente sinistres ne cherche pas un dossier de
 * plus : il
 * cherche celui qui est bloqué. Le filtre par défaut montre donc tout, mais la
 * ligne dit d'emblée à quelle étape reprendre.
 */
export default function Portefeuille() {
  const naviguer = useNavigate();
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState('');
  const [recherche, setRecherche] = useState('');
  const [filtre, setFiltre] = useState('tout');
  const [creation, setCreation] = useState(false);

  useEffect(() => {
    let vivant = true;
    lirePortefeuille()
      .then((d) => vivant && setDonnees(d))
      .catch(() => vivant && setErreur('Impossible de charger vos dossiers.'));
    return () => { vivant = false; };
  }, []);

  const visibles = useMemo(() => {
    const dossiers = donnees?.dossiers || [];
    const mots = recherche.toLowerCase().split(/\s+/).filter(Boolean);
    return dossiers.filter((d) => {
      if (filtre === 'a_reprendre' && !d.etape_manquante) return false;
      if (filtre === 'complets' && d.etape_manquante) return false;
      const texte = [d.client, d.name, d.reference, d.sinistre, d.statut]
        .filter(Boolean).join(' ').toLowerCase();
      return mots.every((mot) => texte.includes(mot));
    });
  }, [donnees, recherche, filtre]);

  const ouvrir = (dossier, etape) =>
    naviguer(`/mon-dossier/${dossier.id}/${etape}`);

  const annuler = async (dossier) => {
    setErreur('');
    try {
      await annulerDossier(dossier.id);
      setDonnees((d) => ({
        ...d, dossiers: d.dossiers.filter((x) => x.id !== dossier.id),
      }));
    } catch {
      setErreur("L'annulation n'a pas abouti.");
    }
  };

  const creer = async () => {
    if (creation) return;
    setCreation(true);
    setErreur('');
    try {
      const dossier = await creerDossier({ name: 'Nouveau sinistre' });
      naviguer(`/mon-dossier/${dossier.id}/sinistre`);
    } catch {
      setErreur("La création n'a pas abouti. Réessayez.");
      setCreation(false);
    }
  };

  return (
    <Layout
      titre="Vos dossiers"
      introduction="Tous les sinistres que vous suivez, du plus récemment modifié au plus ancien. Chaque ligne indique l'étape à reprendre."
      actions={
        <Button variant="contained" size="large" onClick={creer}
                disabled={creation}>
          Ouvrir un dossier
        </Button>
      }
    >
      {erreur && <Alert severity="error" sx={{ mb: 3 }}>{erreur}</Alert>}
      {donnees === null && !erreur && <CircularProgress size={28} />}

      {donnees && (
        <Stack spacing={3}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}
                 alignItems={{ sm: 'center' }} justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              {donnees.dossiers.length} dossier{donnees.dossiers.length > 1 ? 's' : ''}
              {donnees.en_cours > 0 && ` · ${donnees.en_cours} à reprendre`}
              {' · '}{euros(donnees.total)} chiffrés au total
            </Typography>
            <ToggleButtonGroup exclusive size="small" value={filtre}
                               onChange={(e, v) => v && setFiltre(v)}>
              <ToggleButton value="tout" sx={{ textTransform: 'none' }}>
                Tous
              </ToggleButton>
              <ToggleButton value="a_reprendre" sx={{ textTransform: 'none' }}>
                À reprendre
              </ToggleButton>
              <ToggleButton value="complets" sx={{ textTransform: 'none' }}>
                Complets
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          <TextField
            label="Rechercher"
            placeholder="Nom du client, référence, nature du sinistre…"
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            sx={{ maxWidth: 420 }}
          />

          {visibles.map((d) => (
            <Dossier key={d.id} dossier={d} onOuvrir={ouvrir}
                     onInviter={(x) => naviguer(`/mon-dossier/${x.id}/partage`)}
                     onAnnuler={annuler} />
          ))}

          {visibles.length === 0 && (
            <Alert severity="info">
              {donnees.dossiers.length === 0
                ? "Vous ne suivez aucun dossier pour l'instant."
                : 'Aucun dossier ne correspond à cette recherche.'}
            </Alert>
          )}
        </Stack>
      )}
    </Layout>
  );
}
